(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const video = document.getElementById('heroVideo');
  const motion = document.querySelector('[data-motion]');
  const sound = document.querySelector('[data-sound]');
  const soundLabel = sound?.querySelector('[data-sound-label]');
  const sceneMotion = document.querySelector('[data-scene-motion]');
  const motionButtons = [motion, sceneMotion].filter(Boolean);
  let paused = reducedMotion.matches;
  let heroVisible = true;
  function syncVideo() {
    if (!video) return;
    if (paused || document.hidden || !heroVisible) video.pause();
    else video.play().catch(() => {
      // A resumed tab may require a new gesture for sound. Keep autoplay available.
      if (!video.muted) {
        video.muted = true;
        syncSound();
        video.play().catch(() => {});
      }
    });
  }
  function syncSound() {
    if (!video || !sound) return;
    const on = !video.muted && video.volume > 0;
    sound.setAttribute('aria-pressed', String(on));
    sound.setAttribute('aria-label', on ? 'Silenciar el video' : 'Activar sonido del video');
    sound.classList.toggle('is-on', on);
    soundLabel.textContent = on ? 'Silenciar' : 'Activar sonido';
  }
  sound?.addEventListener('click', () => {
    if (!video) return;
    const enable = video.muted || video.volume === 0;
    video.muted = !enable;
    if (enable) {
      if (video.volume === 0) video.volume = 1;
      setPaused(false);
    }
    syncSound();
  });
  video?.addEventListener('volumechange', syncSound);
  syncSound();
  function setPaused(value) {
    paused = value;
    document.documentElement.dataset.motionPaused = String(paused);
    motionButtons.forEach(button => {
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? 'Reanudar movimiento' : 'Pausar movimiento');
      button.innerHTML = paused ? 'Reanudar <span aria-hidden="true">▷</span>' : 'Pausar <span aria-hidden="true">Ⅱ</span>';
    });
    window.dispatchEvent(new CustomEvent('underheat:motion', { detail: { paused } }));
    syncVideo();
  }
  motionButtons.forEach(button => button.addEventListener('click', () => setPaused(!paused)));
  window.addEventListener('underheat:motion-request', event => {
    if (typeof event.detail?.paused === 'boolean') setPaused(event.detail.paused);
  });
  reducedMotion.addEventListener('change', event => setPaused(event.matches));
  document.addEventListener('visibilitychange', syncVideo);
  if (video) {
    new IntersectionObserver(entries => { heroVisible = entries[0].isIntersecting; syncVideo(); }, { threshold: .05 }).observe(video);
    video.addEventListener('error', () => { if (motion) motion.hidden = true; if (sound) sound.hidden = true; });
  }
  setPaused(paused);

  // The mascot shares the page's motion preference, but only fetches and plays nearby.
  const mascot = document.querySelector('#invisible[data-mascot-state]');
  const mascotVideo = mascot?.querySelector('[data-mascot-video]');
  if (mascot && mascotVideo) {
    const mascotMotion = mascot.querySelector('[data-mascot-motion]');
    const mascotMotionLabel = mascot.querySelector('[data-mascot-motion-label]');
    const mascotViews = [...mascot.querySelectorAll('[data-mascot-view]')];
    const mascotViewControls = mascot.querySelector('.mascot-view-switch');
    const mascotCutaway = mascot.querySelector('[data-mascot-cutaway]');
    const mascotCaptions = [...mascot.querySelectorAll('[data-mascot-caption]')];
    const mascotSources = [...mascotVideo.querySelectorAll('source')];
    let mascotView = mascot.dataset.mascotState === 'system' ? 'system' : 'comfort';
    let mascotNear = false;
    let mascotVisible = false;
    let mascotLoaded = false;
    let mascotFailed = false;
    let mascotBlocked = false;
    let mascotExplicitStart = false;
    let mascotPlayPending = false;
    let mascotPlayAttempt = 0;

    mascotVideo.muted = true;

    function mascotCanPlay() {
      return mascotLoaded && !mascotFailed && !mascotBlocked && !paused &&
        !document.hidden && mascotVisible && mascotView === 'comfort';
    }
    function updateMascotControls() {
      const stopped = paused || mascotBlocked;
      mascot.dataset.mascotPlaying = String(mascotCanPlay() && !mascotVideo.paused);
      mascot.dataset.mascotError = String(mascotFailed);
      mascotVideo.setAttribute('aria-hidden', String(mascotFailed || mascotView !== 'comfort'));
      if (mascotMotion) {
        mascotMotion.hidden = mascotFailed || mascotView !== 'comfort';
        mascotMotion.setAttribute('aria-pressed', String(stopped));
        mascotMotion.setAttribute('aria-label', stopped ? 'Reproducir animación' : 'Pausar animación');
      }
      if (mascotMotionLabel) mascotMotionLabel.textContent = stopped ? 'Reproducir' : 'Pausar';
    }
    function stopMascot() {
      // Invalidates pending play promises before pause() can reject them.
      mascotPlayAttempt += 1;
      mascotPlayPending = false;
      mascotVideo.pause();
    }
    function failMascot() {
      mascotFailed = true;
      stopMascot();
      mascotVideo.hidden = true;
      updateMascotControls();
    }
    function loadMascot() {
      if (mascotLoaded || mascotFailed) return;
      const sourceURL = mascotVideo.dataset.src || mascotVideo.getAttribute('src') ||
        mascotSources.map(source => source.dataset.src || source.getAttribute('src')).find(Boolean);
      if (!sourceURL) { failMascot(); return; }
      mascotLoaded = true;
      // Assign the video directly: an empty <source> can error before lazy loading.
      mascotVideo.src = sourceURL;
      mascotVideo.load();
    }
    function syncMascot() {
      if (!mascotFailed && !paused && !mascotBlocked && !document.hidden &&
          mascotView === 'comfort' && (mascotNear || mascotExplicitStart)) loadMascot();
      if (!mascotCanPlay()) {
        stopMascot();
        updateMascotControls();
        return;
      }
      if (mascotPlayPending || !mascotVideo.paused) { updateMascotControls(); return; }
      const attempt = ++mascotPlayAttempt;
      mascotPlayPending = true;
      // Promise.resolve also supports older implementations returning no play promise.
      let playback;
      try { playback = mascotVideo.play(); }
      catch (error) { playback = Promise.reject(error); }
      Promise.resolve(playback).then(() => {
        if (attempt !== mascotPlayAttempt) {
          if (!mascotCanPlay()) mascotVideo.pause();
          return;
        }
        mascotPlayPending = false;
        if (!mascotCanPlay()) mascotVideo.pause();
        updateMascotControls();
      }).catch(error => {
        if (attempt !== mascotPlayAttempt) return;
        mascotPlayPending = false;
        if (mascotVideo.error || error?.name === 'NotSupportedError') { failMascot(); return; }
        if (mascotCanPlay()) mascotBlocked = true;
        updateMascotControls();
      });
      updateMascotControls();
    }
    function updateMascotBounds() {
      const bounds = mascotVideo.parentElement.getBoundingClientRect();
      mascotVisible = bounds.bottom > 0 && bounds.top < innerHeight && bounds.right > 0 && bounds.left < innerWidth;
      mascotNear = bounds.bottom > -innerHeight && bounds.top < innerHeight * 2;
    }
    function setMascotView(view) {
      mascotView = view === 'system' ? 'system' : 'comfort';
      mascot.dataset.mascotState = mascotView;
      mascotViews.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mascotView === mascotView)));
      mascotCaptions.forEach(caption => { caption.hidden = caption.dataset.mascotCaption !== mascotView; });
      mascotCutaway?.setAttribute('aria-hidden', String(mascotView !== 'system'));
      syncMascot();
    }

    mascotViews.forEach(button => button.addEventListener('click', () => setMascotView(button.dataset.mascotView)));
    mascotMotion?.addEventListener('click', () => {
      if (mascotFailed) return;
      if (paused || mascotBlocked) {
        mascotBlocked = false;
        mascotExplicitStart = true;
        updateMascotBounds();
        // Dispatching the shared event synchronizes every motion control without recursion.
        setPaused(false);
        mascotExplicitStart = false;
      } else setPaused(true);
    });
    mascotVideo.addEventListener('error', () => {
      if (mascotLoaded && mascotVideo.error) failMascot();
    });
    mascotVideo.addEventListener('playing', () => {
      if (!mascotCanPlay()) mascotVideo.pause();
      updateMascotControls();
    });
    mascotVideo.addEventListener('pause', updateMascotControls);
    window.addEventListener('underheat:motion', syncMascot);
    document.addEventListener('visibilitychange', syncMascot);

    updateMascotBounds();
    if ('IntersectionObserver' in window) {
      const nearMascot = new IntersectionObserver(entries => {
        mascotNear = entries[0].isIntersecting;
        syncMascot();
      }, { rootMargin: '100% 0px' });
      const visibleMascot = new IntersectionObserver(entries => {
        mascotVisible = entries[0].isIntersecting;
        syncMascot();
      }, { threshold: 0 });
      nearMascot.observe(mascotVideo.parentElement);
      visibleMascot.observe(mascotVideo.parentElement);
    } else {
      const checkMascotPosition = () => { updateMascotBounds(); syncMascot(); };
      window.addEventListener('scroll', checkMascotPosition, { passive: true });
      window.addEventListener('resize', checkMascotPosition, { passive: true });
    }
    setMascotView(mascotView);
    mascot.dataset.mascotReady = 'true';
    if (mascotViewControls) mascotViewControls.hidden = false;
  }

  // Expand only the requested technical detail; the primary journey stays concise.
  document.querySelectorAll('.uh-detail').forEach(detail => detail.addEventListener('toggle', () => {
    window.dispatchEvent(new Event('resize'));
  }));
  function openDestination(target) {
    for (let parent = target.parentElement; parent; parent = parent.parentElement) {
      if (parent.matches('details')) parent.open = true;
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || !document.querySelector('.underheat')) return;
    const id = link.getAttribute('href').slice(1);
    const target = document.getElementById(id === 'proyecto' ? 'underheat-contact' : id);
    if (!target || (!target.closest('.underheat') && !target.closest('#contenido'))) return;
    event.preventDefault();
    openDestination(target);
    document.querySelector('.at-menu[open]')?.close();
    history.replaceState(null, '', '#' + id);
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
    });
  });
  if (location.hash) {
    const target = document.getElementById(location.hash.slice(1));
    if (target) openDestination(target);
  }
  // Keep the originating floor in the inquiry, using text/value APIs only.
  document.querySelector('.at-help__trigger')?.setAttribute('aria-label', 'Ayuda: WhatsApp y chat');
  const params = new URLSearchParams(location.search);
  const productName = params.get('producto')?.slice(0, 160);
  const productHandle = params.get('modelo');
  const floor = { madera: 'Madera de ingeniería', spc: 'Vinílico SPC click' }[params.get('piso')];
  const context = document.querySelector('[data-uh-context]');
  if (context && productName && productHandle && /^[a-z0-9-]+$/.test(productHandle) && floor) {
    context.textContent = 'Tu piso: ' + productName;
    context.hidden = false;
    document.querySelector('[data-uh-product]').value = productName;
    document.querySelector('[data-uh-product-url]').value = 'https://mrparquet.com.uy/products/' + productHandle;
    document.getElementById('contact-floor').value = floor;
  }
  const toggle = document.querySelector('.menu-toggle');
  const nav = document.getElementById('main-navigation');
  function closeMenu() { nav?.classList.remove('is-open'); toggle?.setAttribute('aria-expanded', 'false'); }
  toggle?.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open)); nav?.classList.toggle('is-open', open);
  });
  nav?.addEventListener('click', event => { if (event.target.closest('a')) closeMenu(); });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') { const open = toggle?.getAttribute('aria-expanded') === 'true'; closeMenu(); if (open) toggle.focus(); } });
  window.matchMedia('(min-width: 901px)').addEventListener('change', closeMenu);
  const indexMenu = document.querySelector('[data-underheat-index]');
  indexMenu?.addEventListener('click', event => {
    if (event.target.closest('a')) indexMenu.open = false;
  });
  document.addEventListener('click', event => {
    if (indexMenu?.open && !indexMenu.contains(event.target)) indexMenu.open = false;
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && indexMenu?.open) {
      indexMenu.open = false;
      indexMenu.querySelector('summary').focus();
    }
  });
  const journey = document.getElementById('sistema');
  const stepButtons = [...document.querySelectorAll('[data-journey-step]')];
  const stepCopies = [...document.querySelectorAll('[data-journey-copy]')];
  const progressBar = document.querySelector('[data-journey-progress]');
  function showJourney(progress, stage) {
    stepCopies.forEach((copy, index) => { copy.hidden = index !== stage; });
    stepButtons.forEach((button, index) => { if (index === stage) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current'); });
    if (progressBar) progressBar.style.transform = `scaleX(${progress})`;
  }
  function updateJourney() {
    if (!journey) return;
    const range = Math.max(1, journey.offsetHeight - innerHeight);
    const progress = Math.min(1, Math.max(0, -journey.getBoundingClientRect().top / range));
    showJourney(progress, progress < .29 ? 0 : progress < .74 ? 1 : 2);
  }
  window.addEventListener('underheat:progress', event => showJourney(event.detail.progress, event.detail.stage));
  window.addEventListener('resize', updateJourney, { passive: true });
  stepButtons.forEach(button => button.addEventListener('click', () => {
    if (!journey) return;
    const start = journey.getBoundingClientRect().top + scrollY;
    const distance = Math.max(1, journey.offsetHeight - innerHeight);
    window.scrollTo({ top: start + Number(button.dataset.journeyStep) * distance, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  }));
  const sections = [...document.querySelectorAll('#contenido section')];
  const links = [...document.querySelectorAll('.nav-links a, [data-underheat-index] a')];
  const header = document.querySelector('header.nav');
  const integratedHeader = document.querySelector('.underheat--integrated') && document.querySelector('.at-header--overlay');
  const hero = document.getElementById('hero');
  const layerGuide = document.getElementById('capas');
  let scrollQueued = false;
  function updateSection() {
    const sceneBox = journey?.getBoundingClientRect();
    document.body.classList.toggle('uh-scene-visible', !!sceneBox && sceneBox.height > 0 && sceneBox.top < innerHeight && sceneBox.bottom > 0 && (!journey.closest('details') || journey.closest('details').open));
    if (integratedHeader && hero) integratedHeader.classList.toggle('is-over-hero', hero.getBoundingClientRect().top >= -32);
    if (header && hero) header.classList.toggle('nav--over-hero', hero.getBoundingClientRect().bottom > header.offsetHeight);
    if (header && journey) header.classList.toggle('nav--over-product', journey.getBoundingClientRect().top <= header.offsetHeight && (layerGuide || journey).getBoundingClientRect().bottom > header.offsetHeight);
    const marker = window.innerHeight * .5;
    const current = sections.find(section => { const r = section.getBoundingClientRect(); return r.height > 0 && r.top <= marker && r.bottom > marker; });
    links.forEach(link => { if (link.hash === '#' + current?.id) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
    updateJourney();
    scrollQueued = false;
  }
  window.addEventListener('scroll', () => { if (!scrollQueued) { scrollQueued = true; requestAnimationFrame(updateSection); } }, { passive: true });
  window.addEventListener('resize', updateSection, { passive: true });
  updateSection();

  const plan = document.querySelector('[data-plan-state]');
  const planButtons = [...document.querySelectorAll('[data-plan-view]')];
  const planTabs = document.querySelector('.plan-tabs');
  if (plan && planTabs) {
    const home = document.createComment('Plan view controls');
    planTabs.before(home);
    const compactPlan = matchMedia('(max-width: 760px)');
    const placeControls = () => {
      if (compactPlan.matches) plan.querySelector('.floorplan').before(planTabs);
      else home.after(planTabs);
    };
    compactPlan.addEventListener('change', placeControls);
    placeControls();
  }
  planButtons.forEach(button => button.addEventListener('click', () => {
    if (!plan) return;
    const view = button.dataset.planView;
    plan.dataset.planState = view;
    planButtons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    plan.querySelectorAll('[data-plan-copy]').forEach(copy => { copy.hidden = copy.dataset.planCopy !== view; });
  }));

  // Progressive enhancement keeps all content readable without animation or JS.
  if ('IntersectionObserver' in window && !reducedMotion.matches) {
    const reveal = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('reveal-pending');
      reveal.unobserve(entry.target);
    }), { threshold: .08 });
    document.querySelectorAll('.section-heading,.summary-aside,.origin-signature,.origin-copy,.comfort-heading,.comfort-image,.design-copy,.plan-art,.proof-intro,.proof-sequence,.contact-copy').forEach(element => {
      element.dataset.reveal = '';
      if (element.getBoundingClientRect().top > innerHeight) element.classList.add('reveal-pending');
      reveal.observe(element);
    });
  }

  // The 3D scene (Three.js + texture, ~1.2 MB) loads only when the visitor approaches it.
  const sceneHost = document.querySelector('[data-scene-src]');
  if (sceneHost) {
    let sceneLoaded = false;
    const loadScene = () => {
      if (sceneLoaded) return;
      sceneLoaded = true;
      import(new URL(sceneHost.dataset.sceneSrc, document.baseURI).href).catch(() => document.documentElement.classList.add('underheat-no-webgl'));
    };
    if ('IntersectionObserver' in window) {
      const near = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        near.disconnect();
        loadScene();
      }, { rootMargin: '100px 0px' });
      near.observe(sceneHost);
    } else {
      loadScene();
    }
  }

  // Load the footer character only when its own stage approaches the viewport.
  function prepareFooterVideo(scope = document) {
    const host = scope.querySelector('[data-footer-video-src]');
    if (!host || host.dataset.catScheduled) return;
    host.dataset.catScheduled = 'true';
    const loadCat = () => import(new URL(host.dataset.footerVideoSrc, document.baseURI).href)
      .then(module => { if (host.isConnected) module.mountFooterVideo(host); })
      .catch(() => { host.dataset.catState = 'fallback'; });
    if ('IntersectionObserver' in window) {
      const near = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        stopWatching();
        loadCat();
      }, { rootMargin: '80% 0px' });
      const stopWatching = () => {
        near.disconnect();
        document.removeEventListener('shopify:section:unload', onUnload);
      };
      const onUnload = event => {
        if (event.target.contains(host)) {
          stopWatching();
          delete host.dataset.catScheduled;
        }
      };
      near.observe(host);
      document.addEventListener('shopify:section:unload', onUnload);
    } else loadCat();
  }
  prepareFooterVideo();
  document.addEventListener('shopify:section:load', event => prepareFooterVideo(event.target));

  // Standalone preview prepares a draft. Shopify uses its native contact form.
  const form = document.querySelector('[data-preview-contact]');
  form?.addEventListener('submit', event => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const message = [
      'Hola, quisiera diseñar un sistema UnderHeat para mi proyecto.', '',
      `Nombre: ${data.get('contact[name]') || ''}`,
      `Email: ${data.get('contact[email]') || ''}`,
      `Teléfono: ${data.get('contact[phone]') || ''}`,
      `Tipo de piso: ${data.get('contact[Tipo de piso]') || ''}`, '',
      String(data.get('contact[body]') || '')
    ].join('\n');
    const link = form.querySelector('[data-email-draft]');
    link.href = `mailto:info@mrparquet.com.uy?subject=${encodeURIComponent('Proyecto UnderHeat — Mr. Parquet')}&body=${encodeURIComponent(message)}`;
    form.querySelector('[data-contact-response]').hidden = false;
  });
  form?.addEventListener('input', () => { form.querySelector('[data-contact-response]').hidden = true; });
})();
